import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

const ANALYTICS_TARGET = 'REPOSITORY_SAVED_VIEW'

export const SAVED_VIEW_EVENTS = {
  indexVisited: 'repository_saved_view.index',
  newVisited: 'repository_saved_view.new',
  viewOpened: 'repository_saved_view.open',
  editOpened: 'repository_saved_view.edit',
  viewUpdated: 'repository_saved_view.update',
} as const

export const SAVED_VIEW_OPEN_SOURCES = {
  index: 'index',
  direct: 'direct',
  created: 'created',
} as const

export type SavedViewOpenSource = (typeof SAVED_VIEW_OPEN_SOURCES)[keyof typeof SAVED_VIEW_OPEN_SOURCES]

export type SavedViewOpenLocationState = {savedViewOpenSource?: SavedViewOpenSource}

export const SAVED_VIEW_UPDATE_SOURCES = {
  edit: 'edit',
  query: 'query',
} as const

export type SavedViewUpdateSource = (typeof SAVED_VIEW_UPDATE_SOURCES)[keyof typeof SAVED_VIEW_UPDATE_SOURCES]

export function useSavedViewAnalytics() {
  const {sendAnalyticsEvent} = useAnalytics()

  const trackIndexVisited = useCallback(() => {
    sendAnalyticsEvent(SAVED_VIEW_EVENTS.indexVisited, ANALYTICS_TARGET)
  }, [sendAnalyticsEvent])

  const trackNewVisited = useCallback(() => {
    sendAnalyticsEvent(SAVED_VIEW_EVENTS.newVisited, ANALYTICS_TARGET)
  }, [sendAnalyticsEvent])

  const trackViewOpened = useCallback(
    (shortcutId: number, source: SavedViewOpenSource, openedByCreator: boolean) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewOpened, ANALYTICS_TARGET, {
        shortcut_id: shortcutId,
        source,
        opened_by_creator: openedByCreator,
      })
    },
    [sendAnalyticsEvent],
  )

  const trackEditOpened = useCallback(
    (shortcutId: number) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.editOpened, ANALYTICS_TARGET, {shortcut_id: shortcutId})
    },
    [sendAnalyticsEvent],
  )

  const trackViewUpdated = useCallback(
    (shortcutId: number, source: SavedViewUpdateSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewUpdated, ANALYTICS_TARGET, {shortcut_id: shortcutId, source})
    },
    [sendAnalyticsEvent],
  )

  return {trackIndexVisited, trackNewVisited, trackViewOpened, trackEditOpened, trackViewUpdated}
}
